import { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { CircularProgress, Typography } from '@mui/material';
import {
  FlexColumn,
  Flex,
  Button,
  Switch,
} from '@elementary/common-ui/components';
import {
  fontSize,
  fontWeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';
import { useSso } from '../hooks/useSso';
import SsoConfigDialog from './SsoConfigDialog';
import DomainChip from './DomainChip';

const SectionContainer = styled(FlexColumn)`
  border: 1px solid ${({ theme }) => theme.palette.border.subtle};
  border-radius: ${radius('md')};
  padding: ${space('xxl', 'xxxl')};
  gap: ${space('lg')};
`;

const SectionHeader = styled(Flex)`
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled(Flex)`
  align-items: center;
  gap: ${space('lg')};
`;

const HeaderRight = styled(Flex)`
  align-items: center;
  gap: ${space('lg')};
`;

const DomainsList = styled(FlexColumn)`
  gap: ${space('sm')};
`;

const DomainRow = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.palette.border.subtle};
  border-radius: ${radius('md')};
  padding: ${space('lg', 'xl')};
  justify-content: space-between;
  align-items: center;
`;

const SsoSection = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const sso = useSso();

  useEffect(() => {
    setEnabled(sso.data?.enabled ?? false);
  }, [sso.data?.enabled]);

  const handleToggle = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      setEnabled(event.target.checked);
      await sso.enable({ enabled: event.target.checked });
    },
    [sso],
  );

  const handleOpenDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const domains = sso.data?.domains ?? [];
  const hasDomains = domains.length > 0;

  return (
    <>
      <SectionContainer>
        <SectionHeader>
          <HeaderLeft>
            <Typography sx={{ fontWeight: fontWeight('semibold') }}>
              SSO
            </Typography>
          </HeaderLeft>
          <HeaderRight>
            {sso.isLoading && <CircularProgress size={14} />}
            {sso.data && (
              <Switch
                checked={enabled}
                onChange={handleToggle}
                disabled={sso.isLoading}
              />
            )}
            <Button variant="outlined" onClick={handleOpenDialog}>
              Configure
            </Button>
          </HeaderRight>
        </SectionHeader>

        {hasDomains && (
          <>
            <Typography
              sx={{
                fontSize: fontSize('sm'),
                color: 'content.tertiary',
                fontWeight: fontWeight('medium'),
              }}
            >
              Domains
            </Typography>
            <DomainsList>
              {domains.map((domain) => (
                <DomainRow key={domain.domain}>
                  <Typography>{domain.domain}</Typography>
                  <DomainChip
                    status={domain.validated ? 'validated' : 'pending'}
                  />
                </DomainRow>
              ))}
            </DomainsList>
          </>
        )}
      </SectionContainer>

      <SsoConfigDialog open={dialogOpen} onClose={handleCloseDialog} />
    </>
  );
};

export default SsoSection;
